import { Theme } from "@material-ui/core";
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { IGoogleMapsMarkerPopup } from "./IGoogleMapsMarkerPopup";
import { GoogleMapControls, IMarkerBase } from "./types";
import {
  Cluster,
  MarkerClusterer,
  Renderer,
} from "@googlemaps/markerclusterer";
import { IDashboardMapSettings } from "../dashboard";
interface IMarker extends IMarkerBase {
  googleMarker: google.maps.Marker;
  infoWindow?: IGoogleMapsMarkerPopup;
  mouseOverListener?: google.maps.MapsEventListener;
  mouseOutListener?: google.maps.MapsEventListener;
  mouseClickListener?: google.maps.MapsEventListener;
}

const getDistanceFromBottom = (
  map: google.maps.Map,
  markerLatLng: google.maps.LatLng
) => {
  const projection = map.getProjection();
  const zoom = map.getZoom();
  const southWest = map.getBounds()?.getSouthWest();

  if (!projection) {
    return undefined;
  }

  if (!zoom) {
    return undefined;
  }

  if (!southWest) {
    return undefined;
  }

  var bottomLeft = projection.fromLatLngToPoint(southWest);
  if (!bottomLeft) {
    return undefined;
  }

  var scale = Math.pow(2, zoom);
  var worldPoint = projection.fromLatLngToPoint(markerLatLng);
  if (!worldPoint) {
    return undefined;
  }

  return Math.floor((bottomLeft.y - worldPoint.y) * scale);
};

export const useImperativeGoogleMap = (
  ref: Ref<GoogleMapControls>,
  theme: Theme,
  map?: google.maps.Map,
  settings: IDashboardMapSettings | undefined = { clusteringEnabled: true }
) => {
  const markersRef = useRef<Map<number | string, IMarker>>();
  const markerClusterer = useRef<MarkerClusterer>();
  const currentOpenInfoWindowRef = useRef<IGoogleMapsMarkerPopup | null>(null);

  useEffect(() => {
    if (!map) return;

    const zoomChangedListener = map.addListener("zoom_changed", () => {
      if (currentOpenInfoWindowRef.current) {
        currentOpenInfoWindowRef.current.setMap(null);
        currentOpenInfoWindowRef.current = null;
      }
    });

    return () => {
      google.maps.event.removeListener(zoomChangedListener);
    };
  }, [map]);

  const addMarker = useCallback(
    (marker: IMarker) => {
      if (!markersRef.current) {
        return;
      }

      if (!map) {
        return;
      }

      if (marker.onClick) {
        marker.mouseClickListener = marker.googleMarker.addListener(
          "click",
          () => {
            marker.onClick();
          }
        );
      }

      if (marker.infoWindowContent) {
        import("./GoogleMapMarkerPopup").then((x) => {
          marker.infoWindow = new x.GoogleMapMarkerPopup({
            lat: marker.lat,
            lng: marker.lng,
            paperColor: theme.palette.background.paper,
            content: marker.infoWindowContent!,
          });

          marker.mouseOverListener = marker.googleMarker.addListener(
            "mouseover",
            () => {
              const distance = getDistanceFromBottom(
                map,
                new google.maps.LatLng({ lat: marker.lat, lng: marker.lng })
              );
              const arrowPosition = !distance
                ? "top"
                : distance > 340
                ? "top"
                : "bottom";

              if (currentOpenInfoWindowRef.current) {
                currentOpenInfoWindowRef.current.setMap(null);
              }
              currentOpenInfoWindowRef.current = marker.infoWindow ?? null;
              marker.infoWindow!.setArrowPosition(arrowPosition);
              marker.infoWindow!.setMap(map);
            }
          );

          marker.mouseOutListener = marker.googleMarker.addListener(
            "mouseout",
            () => {
              marker.infoWindow!.setMap(null);
            }
          );
        });
      }
      markersRef.current.set(marker.id, marker);
    },
    [map, theme]
  );

  const removeMarker = useCallback((marker: IMarker) => {
    import("./GoogleMapMarkerPopup").then((_) => {
      if (marker.infoWindowContent && !marker.mouseOverListener) {
        console.warn(
          `Not removing the existing info window content! Vehicle '${marker.title}'`
        );
      }

      if (marker.mouseOverListener) {
        marker.infoWindow?.setMap(null);
      }

      if (marker.mouseOutListener) {
        google.maps.event.removeListener(marker.mouseOutListener);
      }

      if (marker.mouseOverListener) {
        google.maps.event.removeListener(marker.mouseOverListener);
      }
    });

    if (marker.mouseClickListener) {
      google.maps.event.removeListener(marker.mouseClickListener);
    }

    marker.googleMarker.setMap(null);
  }, []);

  const getMarkerLabel: (
    title?: string,
    bearing?: number
  ) => google.maps.MarkerLabel | undefined = useCallback(
    (title?: string, bearing?: number) => {
      return title
        ? {
            color:
              theme.palette.type === "dark"
                ? "#8EC3B9"
                : theme.palette.text.secondary,
            text: title,
            className: `marker-label ${
              bearing !== undefined && (bearing <= 120 || bearing >= 280)
                ? "margin-top-minus-20"
                : "margin-top-20"
            }`,
          }
        : undefined;
    },
    [theme]
  );

  const clearMarkers = useCallback(() => {
    if (markersRef.current?.size) {
      markersRef.current.forEach((marker) => {
        removeMarker(marker);
      });

      markersRef.current.clear();
    }
  }, [removeMarker]);

  const clusterIconRenderer: Renderer = useMemo(
    () => ({
      render: (cluster: Cluster) => {
        const count = cluster.count;

        const position = cluster.position;

        const svgIcon = encodeURIComponent(`
        <svg width="30" height="30" xmlns="http://www.w3.org/2000/svg">
          <circle cx="15" cy="15" r="14" fill="#2196F3" stroke="white" stroke-width="2"/>
          <text x="15" y="20" font-family="Arial" font-size="14" font-weight="bold" fill="white" text-anchor="middle">${count}</text>
        </svg>
      `).replace(/#/g, "%23");

        const iconUrl = `data:image/svg+xml,${svgIcon}`;

        return new google.maps.Marker({
          position: position,
          icon: {
            url: iconUrl,
            anchor: new google.maps.Point(15, 15),
          },
        });
      },
    }),
    []
  );

  const initMarkers = useCallback(
    (
      markers: Array<IMarkerBase>,
      settingsOverride: IDashboardMapSettings | undefined = settings
    ) => {
      clearMarkers();
      if (!map) {
        return;
      }

      if (!markersRef.current) {
        markersRef.current = new Map<number, IMarker>();
      }

      const markerList = markers.map((inputMarker) => {
        const { lat, lng, title, iconPath, markerColor, bearing } = inputMarker;
        const googleMarker = new google.maps.Marker({
          position: { lat, lng },
          map,
          title,
          label: getMarkerLabel(inputMarker.title, inputMarker.bearing),
          draggable: false,
          icon: {
            path: iconPath || "M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z",
            anchor: new google.maps.Point(12, 12),
            scaledSize: new google.maps.Size(50, 50),
            fillColor: markerColor || theme.palette.primary.main,
            fillOpacity: 1,
            rotation: bearing,
            strokeWeight: 1,
            strokeColor: theme.palette.text.primary,
          },
        });
        addMarker({ ...inputMarker, googleMarker });
        return googleMarker;
      });

      markerClusterer.current = new MarkerClusterer({
        map: settingsOverride.clusteringEnabled ? map : null,
        markers: markerList,
        renderer: clusterIconRenderer,
      });
    },
    [
      map,
      getMarkerLabel,
      theme,
      addMarker,
      clearMarkers,
      clusterIconRenderer,
      settings,
    ]
  );

  const hideMarkers = useCallback(
    (selector: (marker: IMarker) => boolean) => {
      if (!map) {
        return;
      }

      if (!markersRef.current?.size) {
        return;
      }

      markersRef.current.forEach((marker) => {
        const selectedMarker = selector(marker);
        markerClusterer.current?.[`${selectedMarker ? "remove" : "add"}Marker`](
          marker.googleMarker
        );
        marker.googleMarker.setMap(selectedMarker ? null : map);
      });
    },
    [map]
  );

  const enableClustering = useCallback(() => {
    const markers = Array.from(markersRef.current?.values() ?? []);
    if (map) {
      markerClusterer.current?.setMap(map);
    }

    markerClusterer.current?.clearMarkers();
    initMarkers(markers, { ...settings, clusteringEnabled: true });
  }, [initMarkers, map, settings]);

  const disableClustering = useCallback(() => {
    const markers = Array.from(markersRef.current?.values() ?? []);
    markerClusterer.current?.setMap(null);
    markerClusterer.current?.clearMarkers();
    initMarkers(markers, { ...settings, clusteringEnabled: false });
  }, [initMarkers, settings]);

  const updateMarker: (
    id: string | number,
    func: (marker: IMarkerBase) => IMarkerBase
  ) => void = useCallback(
    (id, func) => {
      if (!map) {
        return;
      }

      if (!markersRef.current?.size) {
        return;
      }

      const foundMarker = markersRef.current.get(id);
      if (!foundMarker) {
        return;
      }
      const inputMarker = func(foundMarker);

      foundMarker.bearing = inputMarker.bearing;
      foundMarker.markerColor = inputMarker.markerColor;
      foundMarker.iconPath = inputMarker.iconPath;
      foundMarker.lat = inputMarker.lat;
      foundMarker.lng = inputMarker.lng;
      foundMarker.googleMarker.setPosition({
        lat: foundMarker.lat,
        lng: foundMarker.lng,
      });
      foundMarker.googleMarker.setIcon({
        path:
          inputMarker.iconPath ??
          "M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z",
        anchor: new google.maps.Point(12, 12),
        scaledSize: new google.maps.Size(50, 50),
        fillColor: inputMarker.markerColor,
        fillOpacity: 1,
        rotation: inputMarker.bearing,
        strokeWeight: 1,
        strokeColor: theme.palette.text.primary,
      });

      if (inputMarker.infoWindowContent) {
        import("./GoogleMapMarkerPopup").then((x) => {
          if (foundMarker.infoWindow) {
            foundMarker.infoWindow.setMap(null); 
          }
          foundMarker.infoWindow = new x.GoogleMapMarkerPopup({
            lat: inputMarker.lat,
            lng: inputMarker.lng,
            paperColor: theme.palette.background.paper,
            content: inputMarker.infoWindowContent!,
          });
        });
      }
    },
    [map, theme]
  );

  const fitBoundsToCoordinates = useCallback(
    (coordinates: Array<{ lat?: number; lng?: number }>) => {
      if (!map) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      for (const coords of coordinates) {
        const { lat, lng } = coords;
        if (!lat || !lng) {
          continue;
        }

        bounds.extend({ lat, lng });
      }

      map.fitBounds(bounds);
      map.panToBounds(bounds);
    },
    [map]
  );

  const fitBoundsToChildren = useCallback(() => {
    if (!map) {
      return;
    }

    if (!markersRef.current?.size) {
      return;
    }

    // If there is only 1 marker, we can just set the map center according to it
    if (markersRef.current.size === 1) {
      const marker = markersRef.current.values().next().value;
      map.setCenter(marker);
      map.setZoom(5);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const usBounds = new google.maps.LatLngBounds(
      { lat: 24.7433195, lng: -124.7844079 },
      { lat: 49.3457868, lng: -66.9513812 }
    );
    markersRef.current.forEach((marker) => {
      if (
        marker.lat >= usBounds.getSouthWest().lat() &&
        marker.lat <= usBounds.getNorthEast().lat() &&
        marker.lng >= usBounds.getSouthWest().lng() &&
        marker.lng <= usBounds.getNorthEast().lng()
      )
        bounds.extend(marker);
    });

    map.fitBounds(bounds);
    map.panToBounds(bounds);
  }, [map]);

  useImperativeHandle<any, GoogleMapControls>(
    ref,
    () => ({
      resetView() {
        fitBoundsToChildren();
      },

      setCenter(coordinates) {
        map?.setCenter(coordinates);
      },

      panToCoordinates(coordinates) {
        fitBoundsToCoordinates(coordinates);
      },

      clearMarkers() {
        clearMarkers();
      },

      initMarkers(markers) {
        initMarkers(markers);
      },

      hideMarkers(selector) {
        hideMarkers(selector);
      },

      updateMarker(id, func) {
        updateMarker(id, func);
      },

      enableClustering() {
        enableClustering();
      },

      disableClustering() {
        disableClustering();
      },
    }),
    [
      map,
      fitBoundsToChildren,
      fitBoundsToCoordinates,
      initMarkers,
      hideMarkers,
      updateMarker,
      clearMarkers,
      enableClustering,
      disableClustering,
    ]
  );

  return { fitBoundsToChildren, fitBoundsToCoordinates };
};
